import React from 'react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Cell, Legend } from 'recharts';
import { inr } from './ProfitWaterfall';
import TargetPanel from './TargetPanel';

const short = (n) => {
  const a = Math.abs(n);
  const s = a >= 1e7 ? `${(a / 1e7).toFixed(1)}Cr` : a >= 1e5 ? `${(a / 1e5).toFixed(1)}L` : a >= 1e3 ? `${(a / 1e3).toFixed(0)}K` : `${a.toFixed(0)}`;
  return `${n < 0 ? '−' : ''}₹${s}`;
};
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
const gapCls = (g) => (g == null ? 'text-[#6D8E87]' : g >= 0 ? 'text-emerald-700' : 'text-rose-700');

// Net profit per month against the President's target.
export default function BudgetVsActual({ months, totals, targets, canEdit, onSaved }) {
  const data = months.map((m) => ({ ...m, label: monthLabel(m.month) }));
  const hasTarget = totals.target != null;
  return (
    <div className="medical-card p-5 space-y-4" data-testid="budget-vs-actual">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base md:text-lg font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Net profit vs target</h2>
        {hasTarget && (
          <span className={`text-sm font-semibold ${gapCls(totals.gap)}`} data-testid="budget-total-gap">
            {inr(totals.net_profit)} of {inr(totals.target)} target · {totals.gap >= 0 ? 'ahead by' : 'behind by'} {inr(Math.abs(totals.gap))}
          </span>
        )}
      </div>
      <TargetPanel targets={targets} canEdit={canEdit} onSaved={onSaved} />
      <div className="h-64" data-testid="budget-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid stroke="#EEF5F3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6D8E87' }} />
            <YAxis tickFormatter={short} tick={{ fontSize: 11, fill: '#6D8E87' }} width={70} />
            <Tooltip formatter={(v, n) => [inr(v), n]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="net_profit" name="Net profit" fill="#00a69c" radius={[4, 4, 0, 0]}>
              {data.map((d) => <Cell key={d.month} fill={d.target != null && d.net_profit < d.target ? '#F59E9E' : '#00a69c'} />)}
            </Bar>
            {hasTarget && <Line dataKey="target" name="Target" stroke="#052A23" strokeDasharray="5 4" dot={false} strokeWidth={2} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="budget-table">
          <thead className="text-xs text-[#6D8E87] uppercase tracking-wide">
            <tr className="border-b border-[#D5E8E4]">
              {['Month', 'Net profit', 'Target', 'Gap', 'Gap %', 'Cumulative actual', 'Cumulative target'].map((h, i) => <th key={h} className={`py-2 px-3 ${i ? 'text-right' : 'text-left'}`}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.map((m) => (
              <tr key={m.month} className="border-b border-[#EEF5F3]" data-testid={`budget-row-${m.month}`}>
                <td className="py-2 px-3">{m.label}</td>
                <td className="py-2 px-3 text-right tabular-nums">{inr(m.net_profit)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{m.target == null ? '—' : inr(m.target)}</td>
                <td className={`py-2 px-3 text-right tabular-nums font-semibold ${gapCls(m.gap)}`}>{m.gap == null ? '—' : `${m.gap >= 0 ? '+' : ''}${inr(m.gap)}`}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${gapCls(m.gap)}`}>{m.gap_pct == null ? '—' : `${m.gap_pct}%`}</td>
                <td className="py-2 px-3 text-right tabular-nums">{inr(m.cum_actual)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{m.cum_target == null ? '—' : inr(m.cum_target)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
